import { useQuery, type FetchPolicy } from "@apollo/client";
import { useMemo } from "react";
import {
  GetOrderByIdDocument,
  type GetOrderByIdQuery,
} from "../../gql/generated/graphql";
import type { DeepPartial, Order } from "@jasper/client-models";
import { buildMaterialAndShadeCatalog, formatProduct } from "./formatProduct";

interface Props {
  orderId: string;
  skip?: boolean;
  fetchPolicy?: FetchPolicy;
}

const formatOrder = (order: GetOrderByIdQuery["getOrderById"]) => {
  return {
    id: order.id,
    reference: order.reference,
    deliveryDate: order.deliveryDate,
    dentist: {
      id: order.dentist.id,
      firstName: order.dentist.firstName,
      lastName: order.dentist.lastName,
    },
    patient: order.patient
      ? {
          firstName: order.patient.firstName,
          lastName: order.patient.lastName,
          birthDate: order.patient.birthDate
            ? new Date(order.patient.birthDate)
            : undefined,
          sex: order.patient.sex ?? undefined,
        }
      : undefined,
    comments: order.comments.map(comment => ({
      id: comment.id,
      comment: comment.comment,
      createdAt: comment.createdAt,
      files: comment.files.map(file => ({
        id: file.id,
        filename: file.filename,
        url: file.url,
      })),
    })),
    files: order.files.map(file => ({
      id: file.id,
      filename: file.filename,
      url: file.url,
    })),
    dentalOffice: {
      id: order.dentalOffice.id,
      name: order.dentalOffice.name,
      deliveryCarrier: order.dentalOffice.deliveryCarrier ?? undefined,
      pickupCarrier: order.dentalOffice.pickupCarrier ?? undefined,
    },
    products: order.products.map(formatProduct),
  } satisfies DeepPartial<Order> & { id: string };
};

export const useOrderById = ({
  orderId,
  skip,
  fetchPolicy = "cache-first",
}: Props) => {
  const {
    data: getOrderData,
    loading: isGetOrderLoading,
    error: getOrderError,
    refetch,
  } = useQuery(GetOrderByIdDocument, {
    variables: { orderId },
    skip,
    fetchPolicy,
  });

  const isEditable = getOrderData
    ? getOrderData.getOrderById?.status === "DRAFT"
    : undefined;

  // Memoize so `order` keeps a stable reference across renders: `formatOrder`
  // builds a fresh object on every call, which would otherwise make consumers'
  // derived state and reset effects re-run on each render. The material and shade catalog comes
  // from the same response, so it always matches the products of `order`.
  const { order, materialAndShadeCatalog } = useMemo(
    () =>
      getOrderData
        ? {
            order: formatOrder(getOrderData.getOrderById),
            materialAndShadeCatalog: buildMaterialAndShadeCatalog(
              getOrderData.getOrderById.products
            ),
          }
        : { order: undefined, materialAndShadeCatalog: undefined },
    [getOrderData]
  );

  return {
    order,
    materialAndShadeCatalog,
    isLoading: isGetOrderLoading,
    error: getOrderError,
    isEditable,
    refetch,
  };
};
